import React from "react";
import type { Section } from "../../content/schema-types";
import { Button } from "../ui";
import { SectionBadge } from "../ui/SectionBadge";
import { Title } from "../ui/Title";

export type TrustSectionProps = Extract<Section, { type: "trust" }>["content"] & {
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Auf der Startseite steht diese Sektion seit der Kapitel-Fusion im
     * Beweis-Kapitel (`ChapterSection`), und dort ist IHRE Ueberschrift die
     * Kapitelueberschrift — «Software, auf die sich 2.5 Millionen Menschen
     * verlassen» ist die staerkste Zeile der Seite und traegt das Kapitel.
     *
     * `nested` laesst deshalb den ganzen Kopfblock weg: Titel, Untertitel,
     * Beschreibung und Badge stehen eine Ebene hoeher. Uebrig bleibt genau das,
     * was diese Sektion sonst noch ist — das Logoband. Auch Flaeche und
     * Aussenabstand faellt weg; beides setzt das Kapitel.
     *
     * Standard `false`: `/ueber-uns` und die englischen Zwillinge rendern die
     * Sektion eigenstaendig und bleiben unveraendert.
     */
    nested?: boolean;
};

export const TrustSection: React.FC<TrustSectionProps> = ({
    badge,
    badgeVariant = "light",
    title,
    titleHighlight,
    subtitle,
    description,
    ctaButtons,
    logos = [],
    logoCarousel = false,
    nested = false,
}) => {
    // Das Band laeuft endlos, indem die Liste mehrfach hintereinandersteht — die
    // CSS-Animation verschiebt um genau eine Kopie. Vier Kopien wie im
    // `CustomerLogoCarousel`, damit auch breite Viewports nie eine Luecke zeigen.
    const bandLogos = logoCarousel ? [...logos, ...logos, ...logos, ...logos] : logos;
    return (
        // data-testid nach dem Vorbild CornerstonesSection.tsx:53. Ohne Anker laesst sich
        // die Sektion im Test nicht greifen: 34 Komponenten rendern ein <h2>, eine gebaute
        // /loesungen/*-Seite hat neun davon. Ueber den TEXT zu selektieren waere der
        // Nie-rot-Test — er fiele mit demselben Wortlaut, den er bewachen soll.
        <section data-testid="trust-section" className={nested ? "" : "py-8 bg-background-light"}>
            <div className="mx-auto w-full px-4 md:px-12">
                {!nested && (badge || title || titleHighlight || subtitle || description) && (
                    <div className="text-center mb-12">
                        {badge && (
                            <div className="flex justify-center mb-4">
                                <SectionBadge variant={badgeVariant}>{badge}</SectionBadge>
                            </div>
                        )}
                        {(title || titleHighlight) && (
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center">
                                <Title
                                    title={title || titleHighlight}
                                    titleHighlight={title && titleHighlight ? titleHighlight : undefined}
                                    highlightClassName="text-primary"
                                />
                            </h2>
                        )}
                        {subtitle && <p className="text-lg text-gray-600">{subtitle}</p>}
                        {description && <p className="text-lg text-gray-600 mt-4">{description}</p>}
                    </div>
                )}

                {logos && logos.length > 0 && !logoCarousel && (
                    <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap mb-8">
                        {logos.map((logo, index) => (
                            <img
                                key={logo.alt || index}
                                src={logo.src}
                                alt={logo.alt}
                                // eslint-disable-next-line max-len
                                className="h-16 md:h-20 object-contain transition-transform duration-300 ease-in-out hover:scale-110 cursor-pointer opacity-80 hover:opacity-100"
                            />
                        ))}
                    </div>
                )}

                {ctaButtons && ctaButtons.length > 0 && (
                    <div className="text-center">
                        {ctaButtons.map((button, index) => (
                            <Button key={index} href={button.href} variant={button.variant}>
                                {button.text}
                            </Button>
                        ))}
                    </div>
                )}
            </div>

            {/* Das Band steht AUSSERHALB des `container`: es soll randlos laufen, sonst
                waere an beiden Seiten eine harte Kante statt eines Aus-dem-Bild-Laufens.
                `overflow-hidden` schneidet die vier Kopien ab, die CSS-Animation
                verschiebt um genau eine. */}
            {logos && logos.length > 0 && logoCarousel && (
                <div className="relative overflow-hidden w-full mt-4 mb-4" data-testid="trust-logo-carousel">
                    <div className="customer-logo-carousel flex filter grayscale">
                        {bandLogos.map((logo, index) => (
                            <div
                                key={index}
                                className="flex-shrink-0 flex items-center justify-center mx-6 md:mx-8"
                                style={{ minWidth: "160px" }}
                            >
                                <img
                                    src={logo.src}
                                    // Die Kopien 2–4 tragen dieselben Logos noch einmal.
                                    // Nur das erste Viertel wird benannt, der Rest ist
                                    // `aria-hidden` — sonst laese ein Screenreader jeden
                                    // Kundennamen viermal vor.
                                    alt={index < logos.length ? logo.alt : ""}
                                    aria-hidden={index >= logos.length}
                                    className="h-12 md:h-16 w-auto object-contain opacity-80"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

export default TrustSection;
